import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  Grid,
  IconButton,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Settings,
  ViewList,
} from '@mui/icons-material';
import { BotConfig } from '../types';

interface BotCardProps {
  bot: BotConfig;
  onStart: (botName: string) => void;
  onStop: (botName: string) => void;
  onRestart: (botName: string) => void;
  onOpenConfig: (botName: string) => void;
  onViewLogs: (botName: string) => void;
  loading?: boolean;
}

const BotCard: React.FC<BotCardProps> = ({
  bot,
  onStart,
  onStop,
  onRestart,
  onOpenConfig,
  onViewLogs,
  loading = false,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'stopped':
        return 'default';
      case 'error':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online':
        return '🟢 Çalışıyor';
      case 'stopped':
        return '🔴 Durduruldu';
      case 'error':
        return '⚠️ Hatalı';
      default:
        return '🟡 Bilinmiyor';
    }
  };

  const getCardGradient = (status: string) => {
    switch (status) {
      case 'online':
        return 'linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%)';
      case 'stopped':
        return 'linear-gradient(135deg, #ffaaa5 0%, #ff8a80 100%)';
      case 'error':
        return 'linear-gradient(135deg, #ffd3a5 0%, #fd9853 100%)';
      default:
        return 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
    }
  };

  return (
    <div className="floating">
      <Card sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        background: getCardGradient(bot.status),
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.2)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px) scale(1.02)',
          boxShadow: '0 15px 45px rgba(0,0,0,0.25)',
        }
      }} className="modern-card glow-on-hover">
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography 
            variant="h5" 
            component="h2"
            sx={{
              fontWeight: 'bold',
              color: '#2c3e50',
              textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
            }}
          >
            🤖 {bot.name.toUpperCase()}
          </Typography>
          <Chip
            label={getStatusText(bot.status)}
            color={getStatusColor(bot.status) as any}
            size="medium"
            sx={{
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              fontSize: '0.8rem'
            }}
          />
        </Box>

        <Box 
          sx={{
            background: 'rgba(255,255,255,0.7)',
            borderRadius: '10px',
            p: 2,
            mb: 2,
            backdropFilter: 'blur(5px)'
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            📁 Process: {bot.processName}
          </Typography>

          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              wordBreak: 'break-all',
              fontSize: '0.75rem'
            }}
          >
            🗂️ Yol: {bot.path}
          </Typography>
        </Box>

        <Box mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => onStart(bot.name)}
                disabled={bot.status === 'online' || loading}
                sx={{
                  background: 'linear-gradient(45deg, #4caf50, #45a049)',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #45a049, #4caf50)',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    background: '#ccc',
                    color: '#999'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                ▶️ Başlat
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Stop />}
                onClick={() => onStop(bot.name)}
                disabled={bot.status === 'stopped' || loading}
                sx={{
                  background: 'linear-gradient(45deg, #f44336, #d32f2f)',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #d32f2f, #f44336)',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    background: '#ccc',
                    color: '#999'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                ⏹️ Durdur
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => onRestart(bot.name)}
                disabled={loading}
                sx={{
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderWidth: '2px',
                  borderColor: '#2196f3',
                  color: '#2196f3',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #2196f3, #1976d2)',
                    color: 'white',
                    borderColor: '#1976d2',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                🔄 Yeniden Başlat
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box 
          mt={3} 
          display="flex" 
          gap={2} 
          justifyContent="center"
        >
          <IconButton
            onClick={() => onOpenConfig(bot.name)}
            sx={{
              background: 'linear-gradient(45deg, #ff9800, #f57c00)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px',
              boxShadow: '0 4px 15px rgba(255, 152, 0, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #f57c00, #ff9800)',
                transform: 'scale(1.1) rotate(5deg)',
              },
              transition: 'all 0.3s ease'
            }}
            title="Ayarları Düzenle"
          >
            <Settings />
          </IconButton>
          <IconButton
            onClick={() => onViewLogs(bot.name)}
            sx={{
              background: 'linear-gradient(45deg, #9c27b0, #7b1fa2)',
              color: 'white',
              borderRadius: '12px',
              padding: '12px',
              boxShadow: '0 4px 15px rgba(156, 39, 176, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #7b1fa2, #9c27b0)',
                transform: 'scale(1.1) rotate(-5deg)',
              },
              transition: 'all 0.3s ease'
            }}
            title="Logları Görüntüle"
          >
            <ViewList />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
    </div>
  );
};

export default BotCard;
